import { useSyncExternalStore } from 'react';

import { notifyMissionsChanged, onMissionsChanged } from '../lib/mission/missionsChanged';
import { startPeriodicRead } from '../lib/net/periodicRead';
import { createRealtimeSync } from '../lib/realtime/createRealtimeSync';
import { supabase } from '../lib/supabase';
import {
  INITIAL_SIGNAL,
  WAITING_EVENT,
  WAITING_FALLBACK_POLL_MS,
  WAITING_RECONCILE_MS,
  WAITING_TOPIC,
  applyRead,
  parseWaitingState,
  type ReadCause,
  type WaitingSignal,
} from '../lib/workQueue/waitingSignal';

/**
 * One reader of what waits on Gilles for the whole app — the nav badge (every page), the
 * cockpit's list (its keys) and its liveness mark — fed by the server:
 *  - the server pushes `changed` on the private topic `flux:waiting` when what waits may have
 *    moved (triggers `flux_waiting_touch`); the page reads `flux_waiting_state` and, when the
 *    fingerprint moved, sends `missions:changed`: every reader of the cockpit reloads silently;
 *  - a read at each (re)connection, on return to the tab or network (`createRealtimeSync`), and
 *    every minute for what only time moves (a report that ends); every 15 s while the channel
 *    is down;
 *  - a read after each gesture of the page (`missions:changed`), without a second reload.
 * A hidden tab reads nothing on the server's word: it catches up once seen again (the sync re-reads
 * on return to the tab). On 17/10/2026 a computer with eight tabs open read the counter eight times
 * per message, and its cockpits reloaded 664 times in an hour: the base slowed down under them, and
 * every click of a visible tab waited behind.
 */
let signal: WaitingSignal = INITIAL_SIGNAL;
const listeners = new Set<() => void>();
let stop: (() => void) | null = null;
let inFlight: Promise<void> | null = null;
let queued: ReadCause | null = null;
/** Our own broadcast comes back as `missions:changed`: it is not a gesture to re-read for. */
let echo = false;

function emit(next: WaitingSignal) {
  signal = next;
  for (const listener of listeners) listener();
}

async function readOnce(cause: ReadCause) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC absent from the generated types
  const { data, error } = await (supabase.rpc as any)('flux_waiting_state');
  const state = error ? null : parseWaitingState(data);
  // A failed read keeps the last signal: its age makes the page say it no longer follows.
  if (!state) return;
  const { signal: next, changed } = applyRead(signal, state, cause, Date.now());
  emit(next);
  if (changed) {
    echo = true;
    notifyMissionsChanged();
    echo = false;
  }
}

const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** One read at a time; a read asked meanwhile runs right after (a server cause wins). */
function read(cause: ReadCause): Promise<void> {
  if (cause === 'server' && tabHidden()) return Promise.resolve();
  if (inFlight) {
    queued = queued === 'server' ? 'server' : cause;
    return inFlight;
  }
  inFlight = readOnce(cause).finally(() => {
    inFlight = null;
    const next = queued;
    queued = null;
    if (next) void read(next);
  });
  return inFlight;
}

function start(): () => void {
  let stopPolling: (() => void) | null = null;
  const schedule = () => {
    stopPolling?.();
    const every = signal.live ? WAITING_RECONCILE_MS : WAITING_FALLBACK_POLL_MS;
    // The sync below already re-reads at the wake: the poll only ticks while the page is awake.
    stopPolling = startPeriodicRead({ everyMs: every, read: () => void read('server'), catchUpOnWake: false });
  };
  const sync = createRealtimeSync({
    channelName: WAITING_TOPIC,
    broadcast: { event: WAITING_EVENT },
    // Joined, re-joined, back on the tab: catch up on what was missed meanwhile.
    onChange: () => read('server'),
    onStatus: (live) => {
      if (live === signal.live) return;
      emit({ ...signal, live });
      schedule();
    },
  });
  const offGesture = onMissionsChanged(() => {
    if (!echo) void read('gesture');
  });
  schedule();
  sync.subscribe();
  void read('server');
  return () => {
    stopPolling?.();
    offGesture();
    sync.unsubscribe();
    emit({ ...signal, live: false });
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!stop) stop = start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && stop) {
      stop();
      stop = null;
    }
  };
}

export function useWaitingSignal(): WaitingSignal {
  return useSyncExternalStore(subscribe, () => signal);
}

/** Test seam: forget the shared signal between tests. */
export function resetWaitingSignalForTests(): void {
  signal = INITIAL_SIGNAL;
  inFlight = null;
  queued = null;
}
